/**
 * Applies the configured theme as CSS custom properties.
 *
 * The names written here are Tailwind v4's own theme namespaces (`--color-*`,
 * `--radius-*`, `--font-*`). Tailwind emits a `@theme` block with the same names as
 * fallbacks, and every utility it generates compiles down to `var(--color-accent)`
 * and friends. Writing the properties at runtime therefore re-skins both the
 * utilities and the hand-written CSS in one step, no second stylesheet, no
 * `dark:` duplication, no component involvement.
 */
import type { ColorScheme, Palette, ResolvedThemeConfig } from '$lib/config/schema';

/**
 * Palette field → CSS custom property.
 *
 * The right-hand names are Tailwind v4's `--color-*` namespace, so they generate
 * utilities. `text` and `textMuted` are mapped to `fg`/`fg-muted` because the
 * literal mapping would produce `text-text` and `text-text-muted`. Configuration
 * keeps the readable field names; only the emitted property is terse.
 *
 * Keys are checked against `Palette`, so a field added to the schema without a
 * property here is a compile error rather than an invisible colour.
 */
const PALETTE_VARS: Record<keyof Palette, string> = {
	accent: '--color-accent',
	accentContrast: '--color-accent-contrast',
	background: '--color-background',
	surface: '--color-surface',
	surfaceMuted: '--color-surface-muted',
	border: '--color-border',
	text: '--color-fg',
	textMuted: '--color-fg-muted',
	danger: '--color-danger',
	success: '--color-success',
	warning: '--color-warning'
};

/** The scheme actually in effect, once `system` has been resolved. */
export type EffectiveScheme = 'dark' | 'light';

/** Resolves the `system` preference against the OS setting. */
export function resolveScheme(
	preference: ColorScheme,
	systemPrefersDark: boolean
): EffectiveScheme {
	if (preference === 'system') return systemPrefersDark ? 'dark' : 'light';
	return preference;
}

/** True when the OS currently prefers a dark interface. */
export function systemPrefersDark(): boolean {
	if (typeof window === 'undefined' || !window.matchMedia) return true;
	return window.matchMedia('(prefers-color-scheme: dark)').matches;
}

/**
 * Writes a palette onto an element.
 *
 * `data-theme` is set alongside the custom properties so component CSS can react to
 * the active scheme (tuning a shadow, or an image opacity) without reading a colour.
 * `color-scheme` is set too, which is what makes scrollbars, form controls and
 * `select` popups match instead of flashing white on a dark theme.
 */
export function applyTheme(
	theme: ResolvedThemeConfig,
	scheme: EffectiveScheme,
	target: HTMLElement
): void {
	const palette = scheme === 'dark' ? theme.dark : theme.light;

	for (const key of Object.keys(PALETTE_VARS) as (keyof Palette)[]) {
		target.style.setProperty(PALETTE_VARS[key], palette[key]);
	}

	target.style.setProperty('--radius-panel', theme.radius);
	target.style.setProperty('--font-sans', theme.fontSans);
	target.style.setProperty('--font-mono', theme.fontMono);

	target.dataset.theme = scheme;
	target.style.colorScheme = scheme;
}

/**
 * Applies the theme before first paint.
 *
 * Called from `app.html` through a small inline script so a dark-themed site never
 * flashes its light palette on load. Kept dependency-free and defensive: it must
 * never throw, because a failure here would break the whole page.
 */
export function applyStoredSchemeEarly(storageKey: string, fallback: ColorScheme): void {
	try {
		const stored = localStorage.getItem(storageKey);
		const preference: ColorScheme =
			stored === 'dark' || stored === 'light' || stored === 'system' ? stored : fallback;
		const scheme = resolveScheme(preference, systemPrefersDark());
		document.documentElement.dataset.theme = scheme;
		document.documentElement.style.colorScheme = scheme;
	} catch {
		/* Private mode, or storage disabled. The default scheme stands. */
	}
}
